// src/pages/Workshop1.js

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';

const Workshop1 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stripeLink = "https://buy.stripe.com/cNi14o5Jg9jm1Pc4dV9oc0M";

  return (
    <>
      <Helmet>
        <title>Workshop 1 | Little Hibba</title>
        <meta name="description" content="What You Need to Know Before You Start Your Children’s Book" />
      </Helmet>

      <style>{`
        .workshop-hero { background-color: #f2f7fa; padding: 120px 0 60px 0; }
        .classy-heading { font-family: 'Playfair Display', serif; color: #1a365d; }
        .text-reading { font-size: 1.1rem; line-height: 1.8; color: #4a5568; }
        .sticky-card { position: sticky; top: 120px; border: none; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.08); overflow: hidden; }
        .list-icon { color: #d4af37; margin-right: 12px; font-size: 1.2rem; }
        ul.custom-list { list-style: none; padding-left: 0; }
        ul.custom-list li { margin-bottom: 15px; display: flex; align-items: flex-start; }
      `}</style>

      <section className="workshop-hero">
        <Container>
          <Row className="justify-content-center text-center">
            <Col md={10} lg={8}>
              <span className="badge bg-white text-primary border border-primary px-3 py-2 rounded-pill text-uppercase mb-4">Workshop 1</span>
              <h1 className="classy-heading display-4 fw-bold mb-4">What You Need to Know Before You Start Your Children’s Book</h1>
              <p className="lead text-muted">Unsure if your Muslim kids’ book idea will actually work? Get clear on formats, markets and what publishers expect – before you write a word.</p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5 bg-white">
        <Container>
          <Row className="g-5">
            <Col lg={7} className="text-reading">
              <h3 className="classy-heading h2 mb-4">Who it’s for</h3>
              <p className="mb-5">Muslim writers and creators with a story idea but no publishing experience, who want to start right instead of rewriting later.</p>

              <h3 className="classy-heading h2 mb-4">What we’ll cover</h3>
              <ul className="custom-list mb-5">
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>Children’s book formats and specs (picture books, early readers, chapter books): word counts, page counts, illustration ratios.</div></li>
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>How the real children’s market works – parents, schools, libraries, retailers – and where Muslim books fit.</div></li>
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>Mainstream editor expectations translated for faith‑based content: pacing, age bands, avoiding “preachy” pitfalls.</div></li>
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>Mapping your idea to trade, educational or crossover markets with Muslim examples.</div></li>
              </ul>

              <h3 className="classy-heading h2 mb-4">You’ll leave with</h3>
              <ul className="custom-list mb-5">
                <li><i className="bi bi-star-fill list-icon"></i> <div>A one‑page “book brief” template filled out for your idea (format, age, market fit).</div></li>
                <li><i className="bi bi-star-fill list-icon"></i> <div>A shortlist of 5–10 “must‑read” books to benchmark against.</div></li>
                <li><i className="bi bi-star-fill list-icon"></i> <div>Confidence that your project aligns with industry realities and the Muslim family needs.</div></li>
              </ul>
              
              <hr className="my-5" />

              <h3 className="classy-heading h2 mb-4">About Your Instructor</h3>
              <div className="d-flex align-items-center mb-4">
                <img src="/images/sajeda/12.jpg" alt="Sajeda Faraz" className="rounded-circle me-4 shadow-sm" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                <div>
                  <h4 className="mb-1 classy-heading">Sajeda Faraz</h4>
                  <p className="text-muted mb-0">Creative Director & Mentor</p>
                </div>
              </div>
              <p>Led by Sajeda Faraz (that's me!), a creative director and mentor with over 15 years of experience in branding, design, and publishing, this live workshop offers more than theory.</p>
              <p>I’ve helped develop, position, and launch over 400+ children’s titles across indie and traditional publishing, and I know what makes a creator stand out in a crowded market. From writing bios that land school visits to crafting brands that attract publishers, I’ve done it all, and I’m here to walk you through it.</p>
              <p>This is an intimate, in-person experience designed to give you personalised feedback, direct access to expert insights, and the creative clarity you need to move forward with confidence.</p>
            </Col>

            <Col lg={5}>
              <Card className="sticky-card bg-light">
                <img src="/images/sajeda/13.jpg" alt="Workshop" className="img-fluid" style={{ borderBottom: '5px solid #1a365d' }} />
                <Card.Body className="p-4 p-md-5">
                  <h4 className="classy-heading mb-4">Workshop Details</h4>
                  <ul className="custom-list text-muted mb-4 fs-6">
                    <li><i className="bi bi-camera-video list-icon"></i> <div>4 x 60-minute live sessions (recorded for replay).</div></li>
                    <li><i className="bi bi-calendar-check list-icon"></i> <div>Weekly commitment (60 minutes every Thursday evening for 4 weeks).</div></li>
                    <li><i className="bi bi-chat-dots list-icon"></i> <div>Supportive Q&A + feedback on your work with ongoing support from Sajeda.</div></li>
                    <li><i className="bi bi-infinity list-icon"></i> <div>Lifetime access to all materials.</div></li>
                    <li><i className="bi bi-exclamation-triangle list-icon text-danger"></i> <div className="text-danger">I am unable to offer roll-overs of any missed sessions.</div></li>
                  </ul>
                  <div className="bg-white p-3 rounded-3 text-center mb-4 border">
                    <span className="text-muted d-block mb-1">Total Investment</span>
                    <span className="display-5 fw-bold" style={{ color: '#1a365d' }}>£189</span>
                  </div>
                  <a href={stripeLink} target="_blank" rel="noopener noreferrer" className="d-block">
                    <Button variant="primary" size="lg" className="w-100 rounded-pill fw-bold py-3 shadow-sm text-uppercase" style={{ letterSpacing: '1px' }}>Enrol Now via Stripe</Button>
                  </a>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Workshop1;